import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { playSound } from '../../utils/utils';
import DieMiniature from './DieMiniature';
import galaxyBackdrop from './coreboundGalaxyBackdrop.svg';
import {
  createVictoryScene,
  getVictoryFrame,
  VICTORY_SEQUENCE_DURATION_MS,
} from './victoryScene';
import './Victory.css';

const CRACKS = [
  'M50 50 48 43 53 34 48 26 51 17 47 8 49 0',
  'M50 50 54 58 49 65 54 74 50 84 55 94 53 100',
  'M50 50 38 47 30 35 17 32 8 18 0 17',
  'M50 50 62 46 68 33 81 29 88 13 100 9',
  'M50 50 37 58 26 56 16 68 0 74',
  'M50 50 65 56 72 68 86 66 94 80 100 82',
  'M48 26 35 21 31 9 18 0',
  'M68 33 77 39 91 35 100 40',
  'M26 56 21 44 6 44 0 38',
  'M54 74 65 81 64 91 72 100',
  'M37 58 39 72 30 85 32 100',
  'M62 46 74 49 83 44 100 51',
];

const VictorySequence = ({ dieId, faceValues, onComplete }) => {
  const dialogRef = useRef(null);
  const sceneRef = useRef(null);
  const skipRef = useRef(null);
  const completedRef = useRef(false);
  const completeRef = useRef(onComplete);
  const [fallback, setFallback] = useState(false);
  completeRef.current = onComplete;
  const finish = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    completeRef.current();
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    const releaseScroll = lockBodyScroll();
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousHidden = root?.getAttribute('aria-hidden');
    const previousFocus = document.activeElement;
    dialogRef.current?.focus({ preventScroll: true });
    if (root) {
      root.inert = true;
      root.setAttribute('aria-hidden', 'true');
    }
    const scene = createVictoryScene(sceneRef.current, dieId, faceValues);
    scene?.update(0, getVictoryFrame(0));
    setFallback(!scene);
    let animationFrame;
    let start = null;
    let breathed = false;
    let cracked = false;
    let lostContext = false;
    const animate = (now) => {
      if (completedRef.current) return;
      if (start === null) start = now;
      const elapsed = (now - start) / 1000;
      const frame = getVictoryFrame(elapsed);
      const host = dialogRef.current;
      for (const [key, value] of Object.entries(frame))
        host?.style.setProperty(`--victory-${key}`, String(value));
      if (scene && !scene.update(elapsed, frame) && !lostContext) {
        lostContext = true;
        setFallback(true);
      }
      if (!breathed && elapsed >= 0.85) {
        breathed = true;
        playSound('/sounds/whoosh-cinematic-sound.mp3', {
          preventOverlap: true,
        });
      }
      if (!cracked && elapsed >= 3.05) {
        cracked = true;
        playSound('/sounds/treasure-chest-slice-open.mp3', {
          preventOverlap: true,
        });
      }
      if (elapsed * 1000 >= VICTORY_SEQUENCE_DURATION_MS) finishRef.current();
      else animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    // Results remain reachable even if the browser suspends rendering.
    const failsafe = setTimeout(
      () => finishRef.current(),
      VICTORY_SEQUENCE_DURATION_MS + 2000
    );
    return () => {
      clearTimeout(failsafe);
      cancelAnimationFrame(animationFrame);
      scene?.dispose();
      releaseScroll();
      if (root) {
        root.inert = previousInert;
        if (previousHidden === null) root.removeAttribute('aria-hidden');
        else root.setAttribute('aria-hidden', previousHidden);
      }
      if (previousFocus?.isConnected)
        previousFocus.focus?.({ preventScroll: true });
    };
  }, [dieId, faceValues]);

  return createPortal(
    <section
      ref={dialogRef}
      className={`coreboundVictorySequence ${fallback ? 'is-fallback' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="corebound-victory-sequence-title"
      aria-describedby="corebound-victory-sequence-description"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          finish();
        }
        if (event.key === 'Tab') {
          event.preventDefault();
          skipRef.current?.focus();
        }
      }}
    >
      <div
        className="coreboundVictorySequenceScene"
        ref={sceneRef}
        aria-hidden="true"
      >
        <img
          className="coreboundVictorySequenceGalaxy"
          src={galaxyBackdrop}
          alt=""
        />
        {fallback && (
          <div className="coreboundVictoryFallbackDie">
            <DieMiniature dieId={dieId} faceValues={faceValues} />
          </div>
        )}
      </div>
      <div className="coreboundVictoryExhaleCaption" aria-hidden="true">
        <span>THE UNIVERSE EXHALES</span>
        <p>The darkness lets go.</p>
      </div>
      <div className="coreboundVictoryGlass" aria-hidden="true">
        {['left', 'right'].map((side) => (
          <div key={side} className={`coreboundVictoryGlassPane is-${side}`}>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none">
              {CRACKS.map((path) => (
                <path key={path} d={path} pathLength="1" />
              ))}
            </svg>
          </div>
        ))}
      </div>
      <div className="coreboundVictorySequenceCopy">
        <span>SYSTEM LIBERATED</span>
        <h1 id="corebound-victory-sequence-title">The Core is broken!</h1>
        <p id="corebound-victory-sequence-description">
          Your signal opened a way through.
        </p>
      </div>
      <button
        ref={skipRef}
        type="button"
        className="coreboundVictorySequenceSkip"
        onClick={finish}
      >
        Skip to results <span aria-hidden="true">→</span>
      </button>
    </section>,
    document.body
  );
};

export default VictorySequence;
